import {
  ApiException,
  FileInfoDto,
  FileResponse,
  FileType,
  QueryFactory,
  TransportChannelStatusDto,
  TransportMessageStatus,
} from '@lmt/shared-types';
import {
  StatusDateKey,
  StatusKey,
} from '@lmt/unicorn-shared/app/components/patients/profile/cases/types';

import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { downloadAndOpenFile } from '@lmt/unicorn-shared/app/helpers/download-files';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { triggerDownloadFileDialog } from '@lmt/unicorn-shared/app/helpers/file-helper';

import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function getFileNameFromFile(
  file: FileResponse,
  defaultName: string,
): string {
  if (file.headers) {
    const disposition = file.headers['content-disposition'];
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        const filename = matches[1].replace(/['"]/g, '');
        if (filename) {
          return filename;
        }
      }
    }
  }
  return defaultName;
}

export function useFileCallbacks(patientDocument: FileInfoDto) {
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18n = useTranslation();
  const tabs = useTabs();
  const [isLoading, setLoading] = useState<boolean>(false);

  const handleFileError = async (error: unknown) => {
    if (error instanceof ApiException) {
      if (error.status == 404) {
        await modals.showError({
          text: i18n.t('patients.documents.file_not_found'),
        });
      } else {
        await modals.showError({ text: parseError(error) });
      }
    } else {
      await modals.showError({ text: parseError(error) });
    }
  };

  const onOpenFile = useCallback(async () => {
    try {
      await openFile(patientDocument);
    } catch (e) {
      await handleFileError(e);
    }
  }, [patientDocument]);

  const onDownloadFile = useCallback(async () => {
    setLoading(true);
    try {
      await downloadFile(patientDocument);
    } catch (error) {
      await handleFileError(error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, patientDocument]);

  const onOpenDocumentViewer = useCallback(async () => {
    if (patientDocument.type !== FileType.DocumentDraft) {
      return;
    }
    tabs.openTab(
      pages.createPatientDocument.link(
        {
          patientId: patientDocument.patientId!,
        },
        {
          caseId: patientDocument.caseId,
          dataBlockId: patientDocument.dataBlockId,
          visitId: patientDocument.visitId ?? undefined,
          documentId: patientDocument.id,
        },
      ),
    );
  }, [patientDocument]);

  const onClick =
    patientDocument.type === FileType.DocumentDraft
      ? onOpenDocumentViewer
      : isElectron()
        ? onOpenFile
        : onDownloadFile;

  return { isLoading, onClick };
}

export const getDocumentTitle = (documentName: string) =>
  documentName.split('.').slice(0, -1).join('.');

export const getDocumentExtension = (documentName: string) =>
  documentName.substr(documentName.lastIndexOf('.'));

export const defaultExcludedFileTypes = [FileType.Sdbdata];

//Desktop version
export async function openFile(document: FileInfoDto) {
  const file = await QueryFactory.FileClient.downloadFile(document.id);
  const downloadFileName = getFileNameFromFile(file, document.name);
  await downloadAndOpenFile(downloadFileName, file.data);
}

//Web version
export async function downloadFile(document: FileInfoDto) {
  const file = await QueryFactory.FileClient.downloadFile(document.id);
  const downloadFileName = getFileNameFromFile(file, document.name);
  const newBlob = new Blob([file.data], { type: file.data.type });
  triggerDownloadFileDialog(newBlob, downloadFileName);
}

export async function openOrDownloadFile(document: FileInfoDto) {
  if (isElectron()) {
    await openFile(document);
  } else {
    await downloadFile(document);
  }
}

// the same list in the FileService.cs
const invalidFileExtensions = [
  '.exe',
  '.com',
  '.bat',
  '.cmd',
  '.vbs',
  '.vbe',
  '.js',
  '.jse',
  '.wsf',
  '.wsh',
  '.msc',
  '.scr',
  '.pif',
  '.hta',
  '.cpl',
  '.jar',
  '.reg',
  '.dll',
  '.sys',
  '.drv',
  '.lnk',
  '.inf',
  '.adp',
  '.app',
  '.bas',
  '.cer',
  '.crt',
  '.der',
  '.fxp',
  '.hlp',
  '.ins',
  '.isp',
  '.mad',
  '.maf',
  '.mag',
  '.mam',
  '.maq',
  '.mar',
  '.mas',
  '.mat',
  '.mau',
  '.mav',
  '.maw',
  '.mda',
  '.mdb',
  '.mde',
  '.mdt',
  '.mdw',
  '.mdz',
  '.msh',
  '.msh1',
  '.msh2',
  '.mshxml',
  '.msh1xml',
  '.msh2xml',
  '.msi',
  '.msp',
  '.mst',
  '.ops',
  '.pcd',
  '.plg',
  '.prf',
  '.prg',
  '.pst',
  '.scf',
  '.sct',
  '.shb',
  '.shs',
  '.url',
  '.vb',
  '.vsd',
  '.vsmacros',
  '.vss',
  '.vst',
  '.vsw',
  '.ws',
  '.wsc',
];

export function isInvalidExtension(fileName: string) {
  return invalidFileExtensions.includes(
    getDocumentExtension(fileName).toLowerCase(),
  );
}

export function statusKey(
  s: TransportMessageStatus | null | undefined,
  e?: string | null | undefined,
): StatusKey {
  if (e) {
    return StatusKey.Failed;
  }

  switch (s) {
    case TransportMessageStatus.Processed:
      return StatusKey.Successful;
    case e || TransportMessageStatus.Error:
      return StatusKey.Failed;
    case TransportMessageStatus.New:
    case TransportMessageStatus.InWork:
    default:
      return StatusKey.InProcess;
  }
}

export function statusWithDateKey(
  s: TransportMessageStatus | null | undefined,
  e?: string | null | undefined,
): StatusDateKey | null {
  if (e) {
    return StatusDateKey.FailedAt;
  }

  switch (s) {
    case TransportMessageStatus.Processed:
      return StatusDateKey.SuccessfulAt;
    case TransportMessageStatus.Error:
      return StatusDateKey.FailedAt;
    case TransportMessageStatus.New:
    case TransportMessageStatus.InWork:
      return StatusDateKey.InProcessSince;
    default:
      return null;
  }
}

export function aggregateTransportStatus(doc: FileInfoDto): StatusKey | null {
  const statuses = [
    doc.transportInfo?.hL7,
    doc.transportInfo?.gdt,
    doc.transportInfo?.email,
  ] as TransportChannelStatusDto[];

  if (statuses.length === 0) return null;

  let hasInProgress = false;

  for (const s of statuses) {
    if (s?.status === TransportMessageStatus.Error || s?.errorMessage)
      return StatusKey.Failed;
    if (
      s?.status === TransportMessageStatus.New ||
      s?.status === TransportMessageStatus.InWork
    ) {
      hasInProgress = true;
    }
  }

  if (hasInProgress) return StatusKey.InProcess;

  return statuses.some((s) => s?.status === TransportMessageStatus.Processed)
    ? StatusKey.Successful
    : null;
}
